const DEFAULT_FETCH_TIMEOUT_MS = 120_000;

export type FetchWithAuthInit = RequestInit & {
  timeoutMs?: number;
  /**
   * When true, a timeout throws an Error with `name === 'AbortError'` and a
   * message starting with `Request timed out after ` so callers (e.g. the
   * notification-poll loop) can pattern-match on either field cheaply and
   * silently swallow the recoverable race that occurs when the server's
   * idle-close lands inside the client's poll window. Other errors and the
   * non-silent timeout shape are unchanged.
   */
  silent?: boolean;
};

/**
 * Event dispatched when the API returns 401 for a request that should have been
 * authenticated. AuthContext listens for this and performs a client-side logout.
 *
 * Payload:
 *   - url: the request URL that triggered the 401 (string)
 *   - method: the HTTP method used (string, uppercase, defaults to 'GET')
 *
 * Listeners should filter by URL/method to avoid treating expected 401s as session
 * expiry (see AuthContext for the rule set).
 */
export const UNAUTHORIZED_EVENT = 'zenopus:unauthorized' as const;

export type UnauthorizedEventDetail = {
  url: string;
  method: string;
};

const dispatchUnauthorized = (detail: UnauthorizedEventDetail) => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<UnauthorizedEventDetail>(UNAUTHORIZED_EVENT, { detail }));
  } catch {
    // Silently ignore CustomEvent construction errors (very old browsers).
  }
};

/**
 * Event dispatched when the API returns 412 Precondition Failed with the
 * sudo-mode envelope (`code: "sudo_mode_required"` or
 * `"sudo_mode_locked"`). The SudoChallengeModal listens for this and
 * opens the re-auth flow. Detail shape mirrors the server response so
 * the modal can pick the right action class + grant duration without
 * a second round-trip.
 *
 * Kept as a string literal so consumers (hooks/auth/useSudoMode.ts)
 * can reference the same event name without an import cycle.
 */
export const SUDO_REQUIRED_EVENT = 'zenopus:sudo-required' as const;

type SudoRequiredEventDetail = {
  actionClass: string;
  grantDurationSeconds: number;
  retryAfterSeconds: number;
  locked: boolean;
};

/**
 * Inspect a 412 response, parse the envelope, and dispatch the
 * sudo-required event when the body matches. Returns true on dispatch
 * so callers know whether the request should be considered "queued
 * for retry" rather than terminally failed. Reads the body via clone
 * so the caller's `await response.json()` still works.
 */
const maybeDispatchSudoRequired = async (response: Response): Promise<boolean> => {
  if (response.status !== 412) return false;
  let parsed: {
    code?: string;
    actionClass?: string;
    grantDurationSeconds?: number;
    retryAfterSeconds?: number;
  } | null = null;
  try {
    parsed = await response.clone().json();
  } catch {
    return false;
  }
  if (!parsed) return false;
  if (parsed.code !== 'sudo_mode_required' && parsed.code !== 'sudo_mode_locked') {
    return false;
  }
  if (typeof window === 'undefined') return false;
  try {
    const detail: SudoRequiredEventDetail = {
      actionClass: parsed.actionClass ?? 'project_delete',
      grantDurationSeconds: typeof parsed.grantDurationSeconds === 'number' ? parsed.grantDurationSeconds : 0,
      retryAfterSeconds: typeof parsed.retryAfterSeconds === 'number' ? parsed.retryAfterSeconds : 0,
      locked: parsed.code === 'sudo_mode_locked',
    };
    // Regression-guard against the 2026-05-15 silent-failure mode:
    // backend gating shipped, frontend <SudoChallengeListener /> was
    // never mounted, so the dispatched event vanished into the void
    // and the user saw a generic "delete failed" toast instead of the
    // re-auth modal. The dynamic import avoids a hooks→utils cycle
    // (hooks/auth/useSudoMode imports nothing from lib/utils on the
    // hot path) and is gated by NODE_ENV so production bundles don't
    // pay for the warning machinery. console.error chosen over warn
    // because this is a wired-feature-broken bug, not advisory.
    if (process.env.NODE_ENV !== 'production') {
      void import('@/hooks/auth/useSudoMode').then(({ isSudoListenerMounted }) => {
        if (!isSudoListenerMounted()) {
          console.error(
            '[sudo-mode] 412 sudo_mode_required dispatched but no <SudoChallengeListener /> is mounted. ' +
              'Mount it inside <AuthProvider> in app/[locale]/layout.tsx (or another authenticated shell). ' +
              'Without the listener the user only sees a generic error toast and cannot complete the action.',
          );
        }
      });
    }
    window.dispatchEvent(new CustomEvent<SudoRequiredEventDetail>(SUDO_REQUIRED_EVENT, { detail }));
    return true;
  } catch {
    return false;
  }
};

const extractRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  try {
    return (input as Request).url ?? '';
  } catch {
    return '';
  }
};

const extractRequestMethod = (input: RequestInfo | URL, init?: FetchWithAuthInit): string => {
  const raw =
    init?.method ?? (typeof input !== 'string' && !(input instanceof URL) ? (input as Request).method : undefined);
  return typeof raw === 'string' && raw.length > 0 ? raw.toUpperCase() : 'GET';
};

export const getApiAuthHeaders = (headers?: HeadersInit): HeadersInit => {
  const base: Record<string, string> = { 'x-zenopus-client': '1' };
  if (!headers) return base;
  if (headers instanceof Headers) {
    headers.forEach((v, k) => {
      base[k] = v;
    });
    return base;
  }
  if (Array.isArray(headers)) {
    for (const [k, v] of headers) base[k] = v;
    return base;
  }
  return { ...base, ...(headers as Record<string, string>) };
};

const API_BASE_URL = (process.env.NEXT_PUBLIC_API_URL ?? '').trim() || (process.env.NEXT_PUBLIC_API_BASE ?? '').trim();

function inferDevApiBaseUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const { protocol, hostname } = window.location;
    const isLocalhost =
      hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1' || hostname === '[::1]';

    if (!isLocalhost) {
      return '';
    }

    const normalizedHost = hostname === '::1' ? '[::1]' : hostname;
    const fallbackPort = (process.env.NEXT_PUBLIC_API_PORT ?? '').trim() || '8080';
    return `${protocol}//${normalizedHost}:${fallbackPort}`;
  } catch {
    return '';
  }
}

// getApiBaseUrl resolves the base URL the frontend should target for API
// calls. Order matters: a localhost browser session ALWAYS targets a
// loopback Go API, even when NEXT_PUBLIC_API_URL is baked into the build
// pointing at production. This is what fixes audit finding NEW1 — without
// this priority, a dev running `pnpm dev` after a production build would
// cross-origin to https://api.dev.zenopus.de and CORS-block /auth/me.
function getApiBaseUrl(): string {
  return inferDevApiBaseUrl() || API_BASE_URL;
}

function isAbsoluteURL(value: string): boolean {
  return /^https?:\/\//i.test(value) || value.startsWith('//');
}

function withBaseURL(input: RequestInfo | URL): RequestInfo | URL {
  const apiBaseUrl = getApiBaseUrl();

  if (!apiBaseUrl || typeof input !== 'string') {
    return input;
  }

  if (isAbsoluteURL(input)) {
    return input;
  }

  if (input.startsWith('/')) {
    return `${apiBaseUrl}${input}`;
  }

  return `${apiBaseUrl}/${input}`;
}

export const resolveApiUrl = (input: string): string => {
  const resolved = withBaseURL(input);
  return typeof resolved === 'string' ? resolved : resolved.toString();
};

function withTimeoutSignal(signal: AbortSignal | null | undefined, timeoutMs: number) {
  if (timeoutMs <= 0) {
    return {
      signal,
      cleanup: () => {},
      didTimeOut: () => false,
    };
  }

  const controller = new AbortController();
  let timedOut = false;

  const abortFromParent = () => {
    controller.abort(signal?.reason);
  };

  if (signal?.aborted) {
    controller.abort(signal.reason);
  } else if (signal) {
    signal.addEventListener('abort', abortFromParent, { once: true });
  }

  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort(new DOMException(`Request timed out after ${timeoutMs}ms`, 'TimeoutError'));
  }, timeoutMs);

  return {
    signal: controller.signal,
    cleanup: () => {
      clearTimeout(timeoutId);
      if (signal) {
        signal.removeEventListener('abort', abortFromParent);
      }
    },
    didTimeOut: () => timedOut,
  };
}

export const fetchWithAuth = async (input: RequestInfo | URL, init: FetchWithAuthInit = {}) => {
  const { timeoutMs = DEFAULT_FETCH_TIMEOUT_MS, silent = false, signal, ...requestInit } = init;
  const { signal: timeoutSignal, cleanup, didTimeOut } = withTimeoutSignal(signal, timeoutMs);

  try {
    const response = await fetch(withBaseURL(input), {
      ...requestInit,
      signal: timeoutSignal,
      credentials: 'include',
      headers: getApiAuthHeaders(requestInit.headers),
    });

    if (response.status === 401) {
      dispatchUnauthorized({
        url: extractRequestUrl(input),
        method: extractRequestMethod(input, init),
      });
    }

    // Step-up authentication: when a wrapped sensitive route comes back
    // 412 with the sudo envelope, dispatch the SUDO_REQUIRED_EVENT so
    // SudoChallengeModal opens. This runs side-by-side with the caller's
    // own response handling — the response object is returned unchanged
    // so callers can still inspect status / body.
    if (response.status === 412) {
      // Fire-and-forget: we don't await because the dispatch only needs
      // to land before the user reacts to the modal, and reading the
      // body via clone is cheap. Errors are swallowed inside the helper.
      void maybeDispatchSudoRequired(response);
    }

    return response;
  } catch (error) {
    if (didTimeOut()) {
      const err = new Error(`Request timed out after ${timeoutMs}ms`);
      if (silent) {
        // Stamp the AbortError name so silent callers can pattern-match
        // on `err.name === 'AbortError'` without parsing the message.
        err.name = 'AbortError';
      }
      throw err;
    }
    throw error;
  } finally {
    cleanup();
  }
};
